import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import folderService from '../../services/folderService';
import folderD3Service from '../../services/folderD3Service';
import {
  Search,
  ChevronRight,
  Copy,
  Move,
  Trash2,
  Users,
  Check,
  Bot,
  ChevronLeft,
  FolderOpen,
} from 'lucide-react';
import styles from './KnowledgeBaseHub.module.scss';
import KnowledgeBaseHubFolderPanel from './KnowledgeBaseHubFolderPanel';

const AGENTS_PAGE_SIZE = 100;

/**
 * Collect folder IDs from agent KB entries (`knowledgeBase` arrays).
 * @param {unknown[]} entries
 * @returns {string[]}
 */
function collectFolderIdsFromKbEntries(entries) {
  if (!Array.isArray(entries)) return [];
  const seen = new Set();
  const out = [];
  for (const kb of entries) {
    if (!kb || typeof kb !== 'object') continue;
    const raw =
      'knowledgeBase' in kb ? kb.knowledgeBase : 'knowledge_base' in kb ? kb.knowledge_base : undefined;
    const ids = Array.isArray(raw) ? raw : raw != null && raw !== '' ? [raw] : [];
    for (const fid of ids) {
      if (fid == null || fid === '') continue;
      const s = String(fid);
      if (!seen.has(s)) {
        seen.add(s);
        out.push(s);
      }
    }
  }
  return out;
}

/**
 * @param {Record<string, unknown>} agent
 * @returns {{ id: string, name: string, fileCount: number, uses: string[], searchBlob: string, kbFolders: { d1: string[], d3: string[] } }}
 */
function mapApiAgentToHubRow(agent) {
  const id = String(agent?.id ?? '');
  const name = (agent?.name && String(agent.name).trim()) || 'Untitled agent';
  const d1Len = agent?.knowledge_base?.knowledge_bases?.length ?? 0;
  const d3Len = agent?.knowledge_base_dim3?.knowledge_bases?.length ?? 0;
  const fileCount = d1Len + d3Len;
  const f = agent?.features || {};
  const uses = [];
  if (f.knowledge_base) uses.push('Rules book');
  if (f.knowledge_base_d3 || f.customer_data) {
    if (!uses.includes('Customer data')) uses.push('Customer data');
  }
  if (f.agent_chaining) {
    const n = agent?.agent_chaining?.selected_agents?.length ?? 0;
    uses.push(n ? `Chaining (${n})` : 'Chaining');
  }
  if (f.tools) uses.push('Tools');
  if (f.memory) uses.push('Memory');
  if (f.context) uses.push('Context');
  if (uses.length === 0) uses.push('—');
  const searchBlob = `${name} ${agentSearchText(agent)} ${uses.join(' ')}`.toLowerCase();
  const kbFolders = {
    d1: collectFolderIdsFromKbEntries(agent?.knowledge_base?.knowledge_bases),
    d3: collectFolderIdsFromKbEntries(agent?.knowledge_base_dim3?.knowledge_bases),
  };
  return { id, name, fileCount, uses, searchBlob, kbFolders };
}

function agentSearchText(agent) {
  return `${agent?.name || ''} ${agent?.agent_role || ''} ${agent?.agent_goal || ''}`.toLowerCase();
}

/**
 * Order-preserving merge of configured folder IDs with API folder records.
 * @param {string[]} ids
 * @param {unknown[]} apiFolders
 */
function mapFolderIdsToResolved(ids, apiFolders) {
  const list = Array.isArray(apiFolders) ? apiFolders : [];
  const byId = new Map(list.map((f) => [String(f?.id ?? ''), f]));
  return ids.map((id) => {
    const found = byId.get(String(id));
    if (found && typeof found === 'object') {
      return /** @type {{ missing?: boolean }} */ ({ ...found, missing: false });
    }
    return {
      id,
      missing: true,
      name: `Folder (${id})`,
      description: 'Not found in your folders. It may have been removed.',
      created_at: null,
      processingOptions: {},
      vectorStore: '',
    };
  });
}

function AgentKbFolderCard({ folder, onOpen }) {
  const missing = Boolean(folder.missing);
  const createdRaw = folder.created_at ? new Date(folder.created_at) : null;
  const created =
    createdRaw && !Number.isNaN(createdRaw.getTime())
      ? createdRaw.toLocaleDateString()
      : '—';
  const descriptionText =
    typeof folder.description === 'string' ? folder.description.trim() : '';

  return (
    <div className={`${styles.folderHubCard} ${missing ? styles.folderHubCardMissing : ''}`}>
      <div className={styles.folderHubCardTop}>
        <div className={styles.folderHubCardTitleRow}>
          <div className={styles.folderHubCardIcon}>
            <FolderOpen size={20} strokeWidth={2} />
          </div>
          <div>
            <h3 className={styles.folderHubCardName}>{folder.name || 'Folder'}</h3>
            <p className={styles.folderHubCardDate}>Created {created}</p>
          </div>
        </div>
      </div>
      {descriptionText ? (
        <p className={styles.folderHubCardDesc}>{descriptionText}</p>
      ) : null}
      <div className={styles.folderHubCardFooter}>
        <span
          className={`${styles.folderChip} ${missing ? styles.folderChipMuted : styles.folderChipOk}`}
        >
          {missing ? 'Unavailable' : 'Active'}
        </span>
        <button
          type="button"
          className={styles.viewAgentBtn}
          disabled={missing}
          onClick={() => {
            if (!missing) onOpen(folder);
          }}
        >
          View files
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}

function Checkbox({ checked, onChange }) {
  return (
    <button
      type="button"
      className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && <Check size={14} color="#fff" strokeWidth={3} />}
    </button>
  );
}

function AgentKbFoldersPane({ agent, onBack }) {
  const navigate = useNavigate();
  const { d1: d1Ids = [], d3: d3Ids = [] } = agent.kbFolders || {};
  const empty = d1Ids.length === 0 && d3Ids.length === 0;
  const kbKey = JSON.stringify(agent.kbFolders);

  const [loadingFolders, setLoadingFolders] = useState(() => !empty);
  const [resolvedD1, setResolvedD1] = useState(/** @type {unknown[]} */ ([]));
  const [resolvedD3, setResolvedD3] = useState(/** @type {unknown[]} */ ([]));
  const [foldersError, setFoldersError] = useState('');

  useEffect(() => {
    if (empty) {
      setLoadingFolders(false);
      setResolvedD1([]);
      setResolvedD3([]);
      setFoldersError('');
      return undefined;
    }
    let cancelled = false;
    setLoadingFolders(true);
    setFoldersError('');
    (async () => {
      try {
        const [d1Api, d3Api] = await Promise.all([
          folderService.getFolders(),
          folderD3Service.getFolders(),
        ]);
        if (cancelled) return;
        setResolvedD1(mapFolderIdsToResolved(d1Ids, d1Api));
        setResolvedD3(mapFolderIdsToResolved(d3Ids, d3Api));
      } catch (e) {
        if (!cancelled) {
          console.error('AgentKbFoldersPane: failed to load folders', e);
          setFoldersError(e?.message || 'Failed to load folders');
          setResolvedD1([]);
          setResolvedD3([]);
        }
      } finally {
        if (!cancelled) setLoadingFolders(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agent.id, kbKey, empty, d1Ids, d3Ids]);

  const openD1 = (folder) => {
    navigate(`/knowledge-base/folder/${encodeURIComponent(String(folder.id))}`);
  };

  const openD3 = (folder) => {
    navigate(`/knowledge-base-d3/folder/${encodeURIComponent(String(folder.id))}`);
  };

  return (
    <>
      <div className={styles.agentKbFoldersHeader}>
        <button type="button" className={styles.agentKbBackBtn} onClick={onBack}>
          <ChevronLeft size={18} aria-hidden />
          Back to Agents
        </button>
      </div>
      <div className={styles.agentKbFoldersIntro}>
        <h2 className={styles.agentKbFoldersAgentTitle}>{agent.name}</h2>
        <p className={styles.agentKbFoldersIntroLine}>Configured knowledge folders</p>
      </div>
      <div className={styles.agentKbFoldersBody}>
        {empty ? (
          <p className={styles.kbFoldersEmpty}>No folders are linked in this agent&apos;s KB configuration.</p>
        ) : foldersError ? (
          <p className={styles.kbFoldersEmpty}>{foldersError}</p>
        ) : loadingFolders ? (
          <div className={`${styles.emptyState} ${styles.muted}`}>
            <div className={styles.emptyIcon}>
              <FolderOpen size={32} strokeWidth={1.5} />
            </div>
            <p>Loading folders…</p>
          </div>
        ) : (
          <>
            {resolvedD1.length > 0 && (
              <section className={styles.kbFoldersSection} aria-label="Rules book folders">
                <h3 className={styles.kbFoldersSectionTitle}>Company Rules and SOPs </h3>
                <div className={styles.folderCardGrid}>
                  {resolvedD1.map((folder) => (
                    <AgentKbFolderCard key={`d1-${String(folder.id)}`} folder={folder} onOpen={openD1} />
                  ))}
                </div>
              </section>
            )}
            {resolvedD3.length > 0 && (
              <section className={styles.kbFoldersSection} aria-label="Company data folders">
                <h3 className={styles.kbFoldersSectionTitle}>Company data and context </h3>
                <div className={styles.folderCardGrid}>
                  {resolvedD3.map((folder) => (
                    <AgentKbFolderCard key={`d3-${String(folder.id)}`} folder={folder} onOpen={openD3} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

function AgentItem({ agent, isSelected, onToggle, onViewFolders }) {
  const usesLine = agent.uses.join(', ');
  return (
    <div
      className={`${styles.agentRow} ${isSelected ? styles.agentRowSelected : ''}`}
      onClick={() => {
        onViewFolders?.();
      }}
    >
      <div className={styles.fileRowMain}>
        <Checkbox checked={isSelected} onChange={onToggle} />
        <div className={styles.agentAvatar} aria-hidden>
          <Bot className={styles.agentIcon} size={22} strokeWidth={2} />
        </div>
        <div className={styles.fileMeta}>
          <h3>{agent.name}</h3>
          <div className={styles.subRow}>
            <span>
              {agent.fileCount} knowledge {agent.fileCount === 1 ? 'source' : 'sources'}
            </span>
            <span aria-hidden>•</span>
            <span className={styles.usesLine} title={usesLine}>
              Uses: {usesLine}
            </span>
          </div>
        </div>
      </div>
      <button
        type="button"
        className={styles.viewAgentBtn}
        onClick={(e) => {
          e.stopPropagation();
          onViewFolders?.();
        }}
      >
        View
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

/** @typedef {'Agents' | 'D1' | 'D3'} TabType */

export default function KnowledgeBaseHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => {
    const t = (searchParams.get('tab') || 'agents').toLowerCase();
    if (t === 'd1') return /** @type {TabType} */ ('D1');
    if (t === 'd3') return /** @type {TabType} */ ('D3');
    return /** @type {TabType} */ ('Agents');
  }, [searchParams]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [apiAgents, setApiAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [agentsError, setAgentsError] = useState('');
  const [folderViewAgent, setFolderViewAgent] = useState(/** @type {ReturnType<typeof mapApiAgentToHubRow> | null} */ (null));

  useEffect(() => {
    if (activeTab !== 'Agents') setFolderViewAgent(null);
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;

    const loadAllAgents = async () => {
      setLoadingAgents(true);
      setAgentsError('');
      try {
        const first = await apiService.getAgents(1, AGENTS_PAGE_SIZE);
        if (cancelled) return;
        let combined = [...(first?.agents || [])];
        const totalPages = Number(first?.total_pages) || 1;
        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p += 1) {
            pagePromises.push(apiService.getAgents(p, AGENTS_PAGE_SIZE));
          }
          const rest = await Promise.all(pagePromises);
          if (cancelled) return;
          rest.forEach((res) => {
            combined = combined.concat(res?.agents || []);
          });
        }
        setApiAgents(combined);
      } catch (e) {
        if (!cancelled) {
          console.error('KnowledgeBaseHub: failed to load agents', e);
          setApiAgents([]);
          setAgentsError(e?.message || 'Failed to load agents');
        }
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    };

    loadAllAgents();
    return () => {
      cancelled = true;
    };
  }, []);

  const hubAgents = useMemo(() => apiAgents.map(mapApiAgentToHubRow), [apiAgents]);

  const filteredAgents = useMemo(() => {
    if (activeTab !== 'Agents') return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return hubAgents;
    return hubAgents.filter((row) => row.searchBlob.includes(q));
  }, [activeTab, searchQuery, hubAgents]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const currentItems = activeTab === 'Agents' ? filteredAgents : [];
  const isAllSelected =
    activeTab === 'Agents' &&
    currentItems.length > 0 &&
    currentItems.every((item) => selectedIds.has(item.id));

  const toggleAll = () => {
    if (activeTab !== 'Agents') return;
    const allSelected = currentItems.every((item) => selectedIds.has(item.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        currentItems.forEach((item) => next.delete(item.id));
      } else {
        currentItems.forEach((item) => next.add(item.id));
      }
      return next;
    });
  };

  const resetTab = (tab) => {
    setSelectedIds(new Set());
    setFolderViewAgent(null);
    const key = tab === 'Agents' ? 'agents' : tab === 'D1' ? 'd1' : 'd3';
    setSearchParams({ tab: key });
  };

  return (
    <div className={styles.root}>
      <div className={styles.hubTop}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <h1>Knowledge Base</h1>
            <p>All knowledge used across agents and company</p>
          </div>
          <div className={styles.headerActions}>
            {activeTab === 'Agents' && !folderViewAgent && selectedIds.size > 0 && (
              <div className={styles.bulkBar}>
                <span className={styles.bulkLabel}>{selectedIds.size} selected</span>
                <button type="button" className={styles.iconBtn} title="Move" aria-label="Move">
                  <Move size={16} />
                </button>
                <button type="button" className={styles.iconBtn} title="Copy" aria-label="Copy">
                  <Copy size={16} />
                </button>
                <button
                  type="button"
                  className={`${styles.iconBtn} ${styles.danger}`}
                  title="Delete"
                  aria-label="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        </header>

        <div className={styles.inner}>
          <div className={styles.card}>
            <div className={styles.tabsRow}>
              <div className={styles.tabGroup} role="tablist" aria-label="Knowledge categories">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'Agents'}
                  className={`${styles.tabBtn} ${activeTab === 'Agents' ? styles.tabBtnActive : ''}`}
                  onClick={() => resetTab('Agents')}
                >
                  Agent KB
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'D1'}
                  className={`${styles.tabBtn} ${activeTab === 'D1' ? styles.tabBtnActive : ''}`}
                  onClick={() => resetTab('D1')}
                >
                  Company Rules and SOPs
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'D3'}
                  className={`${styles.tabBtn} ${activeTab === 'D3' ? styles.tabBtnActive : ''}`}
                  onClick={() => resetTab('D3')}
                >
                  Company data and context
                </button>
              </div>
              {activeTab === 'Agents' && !folderViewAgent && (
                <div className={styles.searchWrap}>
                  <Search className={styles.searchIcon} aria-hidden />
                  <input
                    type="search"
                    className={styles.searchInput}
                    placeholder="Search agents…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.hubScroll}>
        <div className={styles.inner}>
        {activeTab === 'Agents' ? (
          folderViewAgent ? (
            <div className={styles.listCard}>
              <AgentKbFoldersPane agent={folderViewAgent} onBack={() => setFolderViewAgent(null)} />
            </div>
          ) : (
            <div className={styles.listCard}>
              <div className={styles.listHeader}>
                <div className={styles.listHeaderLeft}>
                  <Checkbox checked={isAllSelected} onChange={toggleAll} />
                  <h2 className={styles.listTitle}>Agent KB</h2>
                </div>
                <span className={styles.countLabel}>
                  {loadingAgents ? 'Loading…' : `${filteredAgents.length} items`}
                </span>
              </div>

              <div>
                {loadingAgents ? (
                  <div className={`${styles.emptyState} ${styles.muted}`}>
                    <div className={styles.emptyIcon}>
                      <Users size={32} />
                    </div>
                    <p>Loading agents…</p>
                  </div>
                ) : agentsError ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <Users size={32} />
                    </div>
                    <p>{agentsError}</p>
                  </div>
                ) : filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => (
                    <AgentItem
                      key={agent.id}
                      agent={agent}
                      isSelected={selectedIds.has(agent.id)}
                      onToggle={() => toggleSelection(agent.id)}
                      onViewFolders={() => setFolderViewAgent(agent)}
                    />
                  ))
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <Users size={32} />
                    </div>
                    <p>No agents found matching your search.</p>
                  </div>
                )}
              </div>
            </div>
          )
        ) : (
          <div className={`${styles.listCard} ${styles.listCardFolder}`}>
            <KnowledgeBaseHubFolderPanel mode={activeTab === 'D1' ? 'd1' : 'd3'} />
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
